// utils
import { List, Map } from 'immutable';
import { call, put, select, takeLeading } from 'redux-saga/effects';
// api
import api from '../../utils/api';
import { openExternalURL } from '../../utils/common';
import {
  clearOverpaySession,
  getOverpaySession,
  saveOverpaySession,
} from '../../utils/overpaySession';
import { toastResponseError } from '../../utils/responseMessageHelper';
import {
  getOutgoing,
  movePackageFromWaitingPaymentToPaid,
} from '../reducers/outgoing';
import {
  CHECKOUT,
  CHECKOUT_CREDIT_CARD,
  CHECKOUT_OVERPAY,
  clearPurchase,
  COMPLETE_ALL_OVERPAY,
  COMPLETE_OVERPAY,
  CONFIRMATION_ALL,
} from '../reducers/purchase';
import { closeRightPanel } from '../reducers/rightPanel';
import {
  outgoingSelectPackingData,
  outgoingSelectPaymentData,
} from '../selectors/outgoingSelectors';

function* onError(error) {
  yield put(clearPurchase());
  toastResponseError(error);
}

function* onConfirmationSuccess(id) {
  yield put(closeRightPanel());
  yield put(movePackageFromWaitingPaymentToPaid(id));
  yield put(clearPurchase());
}

/* worker-saga */
function* onCheckout({ payload }) {
  const { id } = payload;

  try {
    // confirmation
    yield call(api.purchase.confirmation, id);
    yield onConfirmationSuccess(id);
  } catch (error) {
    yield onError(error);
  }
}

function* onCheckoutOverpay({ payload }) {
  const { id } = payload;

  try {
    const origin = window.location.origin;
    const checkoutResponse = yield call(api.purchase.checkoutOverpay, id, {
      success_url: `${origin}/parcels/outgoing/${id}/success`,
      cancel_url: `${origin}/parcels/outgoing/${id}/cancel`,
    });

    const payment_id = checkoutResponse.getIn(['data', 'payment_id']);
    const tracking_id = checkoutResponse.getIn(['data', 'tracking_id']);
    saveOverpaySession(`purchase:${id}`, { payment_id, tracking_id });

    const redirect = checkoutResponse.getIn(['data', 'redirect_url']);
    openExternalURL(redirect);

    yield put(clearPurchase());
  } catch (error) {
    yield onError(error);
  }
}

function* onCheckoutCreditCard({ payload }) {
  const { id, data } = payload;

  try {
    yield call(api.purchase.checkoutCreditCard, id);
    yield call(api.purchase.completeCreditCard, id, data);
    yield call(api.purchase.confirmation, id);
    yield onConfirmationSuccess(id);
  } catch (error) {
    yield onError(error);
  }
}

function* onPaymentCompleteViaOverpay({ payload }) {
  const { id } = payload;
  const payment = getOverpaySession(`purchase:${id}`);
  if (!payment) return;

  try {
    yield call(api.purchase.completeOverpay, id, {
      payment_id: payment.payment_id,
      tracking_id: payment.tracking_id,
    });
    clearOverpaySession(`purchase:${id}`);

    yield call(api.purchase.confirmation, id);
    yield onConfirmationSuccess(id);
  } catch (error) {
    yield onError(error);
  }
}

/* purchase all sagas */
function* onConfirmationAllSuccess() {
  const paymentData = yield select(outgoingSelectPaymentData);
  const inProgressData = yield select(outgoingSelectPackingData);

  yield put(
    getOutgoing(
      Map({
        type: 'waiting_for_payment',
        params: {
          page: 1,
          per_page: paymentData.get('items', List()).size + 1,
        },
      }),
    ),
  );
  yield put(
    getOutgoing(
      Map({
        type: 'paid',
        params: {
          page: 1,
          per_page: 0,
        },
      }),
    ),
  );
  yield put(closeRightPanel());
  yield put(
    getOutgoing(
      Map({
        type: 'packing_in_progress',
        params: {
          page: 1,
          per_page: inProgressData.get('items', List()).size,
        },
      }),
    ),
  );
  yield put(clearPurchase());
}

function* onPaymentConfirmAll({ payload = {} } = {}) {
  try {
    yield call(api.purchase_all.confirmation);
    yield onConfirmationAllSuccess();
  } catch (error) {
    // check if it's confirmation error to checkout
    if (!error.data || !error.data.pay_url || !error.data.sign_id) {
      throw toastResponseError(error);
    }

    const sign_id = error.data.sign_id;
    if (payload.paymentMethod === 'overpay') {
      yield onCheckoutOverpayAll({ sign_id });
    }
  }
}

function* onCheckoutOverpayAll(payload) {
  const { sign_id } = payload;

  try {
    const origin = window.location.origin;
    const checkoutResponse = yield call(api.purchase_all.checkout_overpay, {
      sign_id: sign_id,
      success_url: `${origin}/parcels/outgoing/all/success`,
      cancel_url: `${origin}/parcels/outgoing/all/cancel`,
    });

    const payment_id = checkoutResponse.getIn(['data', 'payment_id']);
    const tracking_id = checkoutResponse.getIn(['data', 'tracking_id']);
    saveOverpaySession('purchase_all', { payment_id, tracking_id, sign_id });

    const redirect = checkoutResponse.getIn(['data', 'redirect_url']);
    openExternalURL(redirect);
  } catch (error) {
    yield onError(error);
  }
}

function* onPaymentCompleteAllViaOverpay() {
  const payment = getOverpaySession('purchase_all');
  if (!payment) return;

  try {
    yield call(api.purchase_all.complete_overpay, {
      payment_id: payment.payment_id,
      tracking_id: payment.tracking_id,
    });
    clearOverpaySession('purchase_all');

    yield call(api.purchase_all.confirmation);
    yield onConfirmationAllSuccess();
  } catch (error) {
    yield onError(error);
  }
}

/* watcher-saga */
function* watchPurchaseActions() {
  yield takeLeading(CHECKOUT, onCheckout);
  yield takeLeading(COMPLETE_OVERPAY, onPaymentCompleteViaOverpay);
  yield takeLeading(CHECKOUT_OVERPAY, onCheckoutOverpay);
  yield takeLeading(CHECKOUT_CREDIT_CARD, onCheckoutCreditCard);
  yield takeLeading(CONFIRMATION_ALL, onPaymentConfirmAll);
  yield takeLeading(COMPLETE_ALL_OVERPAY, onPaymentCompleteAllViaOverpay);
}

export default watchPurchaseActions;
